import React, {forwardRef} from 'react'
import PropTypes from 'prop-types'
import {Link as ChakraLink} from '@chakra-ui/react3'
import {Link as RouterLink, NavLink as RouterNavLink} from 'react-router-dom'
import useMultiSite from '../../../hooks/use-multi-site'
import {isAbsoluteUrl} from '../../../utils/url'

const Link = forwardRef(
    ({href, to, useNavLink = false, styles, isExternal = false, target, rel, ...props}, ref) => {
        const {buildUrl} = useMultiSite()
        const linkTarget = to || href || '/'
        const isAbsolute = isAbsoluteUrl(linkTarget)

        // External and absolute URLs must use regular browser navigation
        // instead of React Router's client-side navigation.
        if (isExternal || isAbsolute) {
            const updatedHref = isAbsolute ? linkTarget : buildUrl(linkTarget)

            return (
                <ChakraLink
                    ref={ref}
                    href={updatedHref}
                    target={isExternal ? target || '_blank' : target}
                    rel={isExternal ? rel || 'noopener' : rel}
                    {...props}
                    {...styles}
                />
            )
        }

        const updatedHref = buildUrl(linkTarget)
        const LinkComponent = useNavLink ? RouterNavLink : RouterLink

        return (
            <ChakraLink
                ref={ref}
                as={LinkComponent}
                {...(useNavLink && {exact: true})}
                {...props}
                to={updatedHref}
                {...styles}
            />
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    Link.displayName = 'Link'
}

Link.propTypes = {
    href: PropTypes.string,
    to: PropTypes.string,
    useNavLink: PropTypes.bool,
    styles: PropTypes.object,
    isExternal: PropTypes.bool,
    target: PropTypes.string,
    rel: PropTypes.string
}

export default React.memo(Link)
